import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

// REACT_APP_KRO_API_BASE includes /api/v1 in prod; strip it so the /api/v1/…
// paths below don't double to /api/v1/api/v1/… (404).
const API_BASE = (process.env.REACT_APP_KRO_API_BASE || 'https://api.kromarketplace.com')
  .replace(/\/api\/v1\/?$/, '');

// SECURITY: no privileged credential is sent from the browser. Studio reads only
// public on-ledger status; settlement writes are server-to-server (CLI) only.
const safeFetch = (url, opts = {}) =>
  fetch(url, { ...opts, headers: { ...(opts.headers || {}) } });

/* ─────────────────────────── Reference data ─────────────────────────── */

const ENTITY = {
  name: 'KRO Brokers, SPC',
  lei: '254900QOF5WI2UKAPE82',
  network: 'canton-mainnet-1',
  validator: 'KRO-Mainnet-1',
  darPackage: 'kro-settlement-0.1.3',
};

// Pilot envelope agreed for the current phase.
const PILOT = {
  notionalCapPerTxn: 10_000_000,
  txnsPerDay: 2,
  windowDays: 10,
  instrument: 'kro.as.accountability.v2 (accountability credits only)',
};

const FACILITY = {
  grossNotional: 910_000_000,
  availableLtv: 755_000_000,
  units: 182,
  navPerUnit: 5_000_000,
  drawnToday: 10_000_000,
  drawsToday: 1,
};

// The four coherent phases every settlement moves through. Replaces the previous
// flat list of separately-navigated workflow screens.
const SETTLEMENT_PHASES = [
  {
    phase: 'Origination',
    icon: 'bi-file-earmark-arrow-up',
    tone: 'warning',
    summary: 'Counterparty presents terms, KRO reviews, and collateral is locked.',
    steps: [
      { label: 'RFQ received', detail: 'Counterparty submits standard terms over the open network.', state: 'complete' },
      { label: 'Terms reviewed', detail: 'KRO reviews and either accepts or returns a counter-proposal.', state: 'complete' },
      { label: 'OpenDraw + allocation lock', detail: 'collateral × (1 − haircut) ≥ notional enforced; CIP-0056 allocation locks both legs.', state: 'complete' },
    ],
  },
  {
    phase: 'Verification',
    icon: 'bi-shield-check',
    tone: 'primary',
    summary: 'Custody quorum and compliance gates clear before anything settles.',
    steps: [
      { label: 'BitGo MPC collateral check', detail: '2-of-3 threshold signature; haircut re-applied on verified NAV.', state: 'complete' },
      { label: 'Compliance gate', detail: 'KYC/AML re-verified and sanctions screening run. KRO does not operate as a clearing agent.', state: 'complete' },
    ],
  },
  {
    phase: 'Settlement',
    icon: 'bi-lightning-charge-fill',
    tone: 'success',
    summary: 'Both legs settle in a single indivisible transaction, or neither does.',
    steps: [
      { label: 'Global Synchronizer cross-sync', detail: 'Both contracts reassigned to the Global Synchronizer so the legs settle together.', state: 'active' },
      { label: 'Atomic DvP — ExecuteTransfer', detail: 'Collateral leg and CIP-56 cash leg settle in one Daml transaction.', state: 'pending' },
    ],
  },
  {
    phase: 'Finality',
    icon: 'bi-patch-check-fill',
    tone: 'info',
    summary: 'Funds return intraday, capacity regenerates, and the receipt is sealed.',
    steps: [
      { label: 'ReturnFunds', detail: 'Repo interest settled at notional × rate × (ACT/360); capacity restored.', state: 'pending' },
      { label: 'CloseOutReceipt + Document Key', detail: 'Immutable receipt anchored on Canton; AES-256 Document Key issued to the counterparty.', state: 'pending' },
    ],
  },
];

const RECENT_SETTLEMENTS = [
  { id: 'pcp-dvp-1781819342633', at: '2026-07-27T14:12:00Z', notional: 10_000_000, counterparty: 'Cumberland DRW (Node 1)', phase: 'Settlement',  status: 'IN_FLIGHT', rate: 4.85, receipt: null },
  { id: 'pcp-dvp-1781733010449', at: '2026-07-27T09:48:00Z', notional: 10_000_000, counterparty: 'Tradeweb Markets',        phase: 'Finality',    status: 'SETTLED',   rate: 4.82, receipt: '00c2db19b7d3…ca78554' },
  { id: 'pcp-dvp-1781646772210', at: '2026-07-26T15:31:00Z', notional: 10_000_000, counterparty: 'Five North Capital',      phase: 'Finality',    status: 'SETTLED',   rate: 4.88, receipt: '00a7f4c1e920…bb31097' },
  { id: 'pcp-dvp-1781560338815', at: '2026-07-26T10:05:00Z', notional: 10_000_000, counterparty: 'Cumberland DRW (Node 2)', phase: 'Finality',    status: 'SETTLED',   rate: 4.80, receipt: '00e18b7a4d55…7c2fe41' },
  { id: 'pcp-dvp-1781473905402', at: '2026-07-25T14:22:00Z', notional: 10_000_000, counterparty: 'Tradeweb Markets',        phase: 'Finality',    status: 'SETTLED',   rate: 4.79, receipt: '00b93c6f2a18…5de0c73' },
];

const NETWORK_NODES = [
  { name: 'Canton Validator — KRO-Mainnet-1', layer: 'Settlement ledger',  uptime: 99.98, latencyMs: 142, state: 'healthy', detail: 'Active contract store reads succeeding; participant node in sync with the Global Synchronizer.' },
  { name: 'Canton Global Synchronizer',        layer: 'Settlement ledger',  uptime: 99.99, latencyMs: 118, state: 'healthy', detail: 'Cross-synchronizer reassignment available; both legs can be co-located for atomic DvP.' },
  { name: 'Provenance — pio-mainnet-1',        layer: 'Collateral records', uptime: 99.95, latencyMs: 210, state: 'healthy', detail: 'Marker state for the three KRO denoms reading cleanly; ZoneScan verification available.' },
  { name: 'BitGo Enterprise — MPC Custody',    layer: 'Custody',            uptime: 99.97, latencyMs: 264, state: 'healthy', detail: '2-of-3 key shares reachable: BitGo HSM, KRO policy engine, BitGo cold backup.' },
  { name: 'Mercury — Payment API',             layer: 'Cash rail',          uptime: 99.91, latencyMs: 338, state: 'healthy', detail: 'ISO 20022 message submission and camt.054 confirmations flowing.' },
  { name: 'Column N.A. — Settlement Bank',     layer: 'Cash rail',          uptime: 99.89, latencyMs: 402, state: 'degraded', detail: 'Elevated response times on wire status polling. No failed instructions; monitoring.' },
];

const MARKERS = [
  { denom: 'kro.as.accountability.v2', label: 'Accountability', units: 182, nav: 5_000_000,  pilot: true },
  { denom: 'kro.gipr.genesis.v2',      label: 'Genesis Bond',   units: 60,  nav: 1_000_000,  pilot: false },
  { denom: 'kro.sc.stewardship.v2',    label: 'Stewardship',    units: 12,  nav: 20_000_000, pilot: false },
];

// Exception records carry a timestamp so they can be tracked and archived weekly.
const ALL_EXCEPTIONS = [
  { at: '2026-07-27T11:04:12Z', phase: 'Settlement',   code: 'GSYNC_REASSIGN_RETRY', severity: 'LOW',      detail: 'Cross-synchronizer reassignment retried once before succeeding. No settlement impact.', resolved: true,  resolvedAt: '2026-07-27T11:04:39Z' },
  { at: '2026-07-26T16:10:00Z', phase: 'Verification', code: 'MPC_QUORUM_TIMEOUT',   severity: 'HIGH',     detail: 'Key share 2 (KRO policy engine) timed out. Draw held, not partially settled. Cleared after policy engine restart.', resolved: true,  resolvedAt: '2026-07-26T16:41:22Z' },
  { at: '2026-07-25T09:45:07Z', phase: 'Verification', code: 'COMPLIANCE_HOLD',      severity: 'CRITICAL', detail: 'Sanctions list refresh flagged a counterparty subsidiary for review. Manual review cleared it as a false positive.', resolved: true,  resolvedAt: '2026-07-25T11:02:44Z' },
  { at: '2026-07-24T13:22:31Z', phase: 'Origination',  code: 'COLLATERAL_STALE_NAV',  severity: 'MEDIUM',   detail: 'Oracle price feed exceeded the 15-minute freshness window, so the adequacy check failed closed. Reconnected and re-evaluated.', resolved: true,  resolvedAt: '2026-07-24T13:39:18Z' },
  { at: '2026-07-27T15:48:00Z', phase: 'Finality',     code: 'RECEIPT_PENDING_RAIL',  severity: 'LOW',      detail: 'Awaiting camt.054 confirmation from the cash rail before the close-out receipt can be sealed.', resolved: false, resolvedAt: null },
];

const SEVERITY_TONE = { CRITICAL: 'danger', HIGH: 'warning', MEDIUM: 'info', LOW: 'secondary' };

// Tools that used to sit as sibling top-level menu entries. Grouped here so the
// hub is the single entry point into the deeper settlement surfaces.
const WORKFLOW_TOOLS = [
  { to: '/kro-exchange/settlement-hub/execution-gateway', icon: 'bi-lightning-fill',        tone: 'warning', title: 'Execution Gateway',  desc: 'Settlement pipeline, ISO 20022 messages, Daml contracts, Document Key, and the end-to-end sandbox test.' },
  { to: '/kro-exchange/settlement-hub/canton-network',    icon: 'bi-diagram-3-fill',        tone: 'primary', title: 'Canton Network',     desc: 'Environment status, RFQ transactions, super-validator registry, node operators, and the live event feed.' },
  { to: '/kro-exchange/settlement-hub/settlement-records',icon: 'bi-folder2-open',          tone: 'info',    title: 'Settlement Records', desc: 'Transaction overview, collateral matrix, compliance and legal records, receipts, and the live audit track.' },
];

/* ─────────────────────────── Helpers ─────────────────────────── */

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n) || 0);

const shortMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return money(v);
};

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const startOfWeek = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
};

/* ─────────────────────────── Component ─────────────────────────── */

const SettlementOperationsHub = () => {
  const [activeTab, setActiveTab] = useState('operations');
  const [showArchived, setShowArchived] = useState(false);

  const [liveStatus, setLiveStatus] = useState(null);
  const [liveSummary, setLiveSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [liveError, setLiveError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => { document.title = 'Settlement Hub — KRO Exchange'; }, []);

  const fetchLive = useCallback(async () => {
    setLoading(true);
    setLiveError(null);
    try {
      const [statusRes, sumRes] = await Promise.all([
        safeFetch(`${API_BASE}/api/v1/canton/network-status?network=mainnet`),
        safeFetch(`${API_BASE}/api/v1/canton/summary`),
      ]);
      if (statusRes.ok) {
        const j = await statusRes.json();
        setLiveStatus(j.status || j);
      } else {
        throw new Error(`network-status returned HTTP ${statusRes.status}`);
      }
      if (sumRes.ok) {
        const j = await sumRes.json();
        setLiveSummary(j.summary || j);
      }
    } catch (e) {
      setLiveError(e.message);
    } finally {
      setLoading(false);
      setLastRefreshed(new Date());
    }
  }, []);

  useEffect(() => {
    fetchLive();
    intervalRef.current = setInterval(fetchLive, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchLive]);

  /* ── Derived ─────────────────────────────────────────────────── */

  const weekStart = startOfWeek();
  const currentExceptions = ALL_EXCEPTIONS.filter(e => new Date(e.at) >= weekStart);
  const archivedExceptions = ALL_EXCEPTIONS.filter(e => new Date(e.at) < weekStart);
  const visibleExceptions = showArchived ? archivedExceptions : currentExceptions;
  const openExceptions = ALL_EXCEPTIONS.filter(e => !e.resolved);

  const settledCount = RECENT_SETTLEMENTS.filter(s => s.status === 'SETTLED').length;
  const inFlightCount = RECENT_SETTLEMENTS.filter(s => s.status === 'IN_FLIGHT').length;
  const settledValue = RECENT_SETTLEMENTS.filter(s => s.status === 'SETTLED').reduce((s, r) => s + r.notional, 0);
  const avgRate = (RECENT_SETTLEMENTS.reduce((s, r) => s + r.rate, 0) / RECENT_SETTLEMENTS.length).toFixed(2);

  const degradedNodes = NETWORK_NODES.filter(n => n.state !== 'healthy');
  const avgUptime = (NETWORK_NODES.reduce((s, n) => s + n.uptime, 0) / NETWORK_NODES.length).toFixed(2);

  const utilisationPct = Math.round((FACILITY.drawnToday / PILOT.notionalCapPerTxn / PILOT.txnsPerDay) * 100);

  const activeContracts = liveStatus?.activeContracts ?? null;
  const lastSettlementDisplay = liveStatus?.lastSettlement?.display ?? null;

  /* ── Presentational helpers ──────────────────────────────────── */

  const KpiCard = ({ icon, label, value, sub, tone }) => (
    <div className="col-6 col-lg-3">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-1">
            <i className={`bi ${icon} text-${tone}`}></i>
            <span className="text-muted text-uppercase" style={{ fontSize: '0.68rem', letterSpacing: '0.6px', fontWeight: 700 }}>{label}</span>
          </div>
          <div className="fw-bold" style={{ fontSize: '1.5rem' }}>{value}</div>
          <small className="text-muted">{sub}</small>
        </div>
      </div>
    </div>
  );

  const StateDot = ({ state }) => {
    const color = state === 'healthy' ? '#198754' : state === 'degraded' ? '#fd7e14' : '#dc3545';
    return <span className="rounded-circle d-inline-block" style={{ width: 8, height: 8, background: color, boxShadow: `0 0 5px ${color}` }} />;
  };

  /* ─────────────────────────── Render ─────────────────────────── */

  return (
    <div className="container-fluid py-4">

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><Link to="/kro-exchange/settlement-hub">KRO Exchange</Link></li>
          <li className="breadcrumb-item active" aria-current="page">Settlement Hub</li>
        </ol>
      </nav>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <h1 className="display-6 fw-bold mb-1">
            <i className="bi bi-bank2 me-2 text-primary"></i>Settlement Hub
          </h1>
          <p className="text-muted mb-2" style={{ fontSize: '0.88rem' }}>
            Operational view of intraday settlement on {ENTITY.network} — status, network health, and exceptions in one place.
          </p>
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill"
            style={{ background: 'rgba(25,135,84,0.08)', border: '1px solid rgba(25,135,84,0.25)', fontSize: '0.72rem' }}>
            <i className="bi bi-shield-check text-success"></i>
            <span className="text-muted">
              KRO operates <strong className="text-success">settlement infrastructure and admin governance</strong> — not a bank, broker-dealer, or clearing agent.
              Settlement is enforced by Daml smart contracts on Canton Network.
            </span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
            {lastRefreshed ? `Refreshed ${lastRefreshed.toLocaleTimeString()}` : 'Fetching…'}
          </small>
          <button className="btn btn-sm btn-outline-primary" onClick={fetchLive} disabled={loading}>
            <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-arrow-clockwise'} me-1`}></i>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Live-read banner */}
      {liveError ? (
        <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '0.8rem' }}>
          <i className="bi bi-exclamation-triangle me-1"></i>
          Canton ledger read unavailable — showing last-known operational state. ({liveError})
        </div>
      ) : (
        <div className="alert alert-light border py-2 mb-3" style={{ fontSize: '0.8rem' }}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <StateDot state="healthy" />
            <strong>Live ledger read</strong>
            <span className="text-muted">·</span>
            <span className="text-muted">{ENTITY.network} · validator {ENTITY.validator} · <code style={{ fontSize: '0.72rem' }}>{ENTITY.darPackage}</code></span>
            {activeContracts != null && (<><span className="text-muted">·</span><span className="text-muted">{activeContracts} active contracts</span></>)}
            {lastSettlementDisplay && (<><span className="text-muted">·</span><span className="text-muted">last settlement {lastSettlementDisplay}</span></>)}
          </div>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-pills mb-4" style={{ gap: '0.25rem' }}>
        {[
          { id: 'operations', icon: 'bi-speedometer2',              label: 'Operations Dashboard' },
          { id: 'status',     icon: 'bi-list-check',                label: 'Settlement Status' },
          { id: 'network',    icon: 'bi-hdd-network',               label: 'Network Health' },
          { id: 'exceptions', icon: 'bi-exclamation-triangle-fill', label: 'Exception Monitor', count: openExceptions.length },
        ].map(t => (
          <li key={t.id} className="nav-item">
            <button className={`nav-link ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
              {t.count > 0 && <span className="badge bg-white text-dark ms-2" style={{ fontSize: '0.62rem' }}>{t.count}</span>}
            </button>
          </li>
        ))}
      </ul>

      {/* ══════════════════ TAB: OPERATIONS DASHBOARD ══════════════════ */}
      {activeTab === 'operations' && (
        <>
          <div className="row g-3 mb-4">
            <KpiCard icon="bi-arrow-left-right"          label="Draws Today"     tone="primary" value={`${FACILITY.drawsToday} / ${PILOT.txnsPerDay}`} sub={`${shortMoney(FACILITY.drawnToday)} drawn · ${utilisationPct}% of daily envelope`} />
            <KpiCard icon="bi-safe"                      label="Facility"        tone="success" value={shortMoney(FACILITY.availableLtv)} sub={`Available against ${shortMoney(FACILITY.grossNotional)} verified notional`} />
            <KpiCard icon="bi-activity"                  label="Network Uptime"  tone="info"    value={`${avgUptime}%`} sub={degradedNodes.length ? `${degradedNodes.length} node(s) degraded` : 'All rails healthy'} />
            <KpiCard icon="bi-exclamation-triangle-fill" label="Open Exceptions" tone={openExceptions.length ? 'danger' : 'success'} value={openExceptions.length} sub={openExceptions.length ? 'Requires attention' : 'Nothing outstanding'} />
          </div>

          <div className="row g-4">
            {/* Settlement lifecycle at a glance */}
            <div className="col-12 col-xl-8">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-bottom py-3">
                  <h6 className="mb-0 fw-bold"><i className="bi bi-diagram-2 me-2 text-primary"></i>Settlement Lifecycle</h6>
                  <small className="text-muted">Every draw moves through the same four phases. Nothing settles until the phase before it clears.</small>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {SETTLEMENT_PHASES.map((p, i) => {
                      const done = p.steps.every(s => s.state === 'complete');
                      const active = p.steps.some(s => s.state === 'active');
                      return (
                        <div key={p.phase} className="col-12 col-md-6">
                          <div className={`border rounded-3 p-3 h-100 ${active ? 'border-primary' : ''}`}>
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <span className={`badge bg-${p.tone}`} style={{ fontSize: '0.62rem' }}>PHASE {i + 1}</span>
                              <span className="fw-bold" style={{ fontSize: '0.9rem' }}><i className={`bi ${p.icon} me-1 text-${p.tone}`}></i>{p.phase}</span>
                              {done && <i className="bi bi-check-circle-fill text-success ms-auto"></i>}
                              {active && <span className="badge bg-primary ms-auto" style={{ fontSize: '0.6rem' }}>IN PROGRESS</span>}
                            </div>
                            <p className="text-muted mb-2" style={{ fontSize: '0.78rem' }}>{p.summary}</p>
                            <ul className="list-unstyled mb-0">
                              {p.steps.map(s => (
                                <li key={s.label} className="d-flex gap-2 mb-2">
                                  <i className={`bi ${s.state === 'complete' ? 'bi-check-circle-fill text-success' : s.state === 'active' ? 'bi-arrow-repeat text-primary' : 'bi-circle text-muted'} flex-shrink-0`} style={{ fontSize: '0.8rem', marginTop: 2 }}></i>
                                  <div>
                                    <div className="fw-semibold" style={{ fontSize: '0.78rem' }}>{s.label}</div>
                                    <div className="text-muted" style={{ fontSize: '0.73rem' }}>{s.detail}</div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Pilot envelope + collateral */}
            <div className="col-12 col-xl-4">
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-header bg-white border-bottom py-3">
                  <h6 className="mb-0 fw-bold"><i className="bi bi-clipboard-data me-2 text-primary"></i>Pilot Envelope</h6>
                </div>
                <div className="card-body" style={{ fontSize: '0.82rem' }}>
                  {[
                    { k: 'Notional cap per transaction', v: money(PILOT.notionalCapPerTxn) },
                    { k: 'Transactions per day',         v: PILOT.txnsPerDay },
                    { k: 'Pilot window',                 v: `${PILOT.windowDays} days` },
                    { k: 'Instrument',                   v: PILOT.instrument },
                  ].map(r => (
                    <div key={r.k} className="d-flex justify-content-between align-items-start py-2 border-bottom">
                      <span className="text-muted">{r.k}</span>
                      <span className="fw-semibold text-end" style={{ maxWidth: '58%' }}>{r.v}</span>
                    </div>
                  ))}
                  <div className="mt-3">
                    <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.76rem' }}>
                      <span className="text-muted">Daily envelope used</span>
                      <span className="fw-semibold">{utilisationPct}%</span>
                    </div>
                    <div className="progress" style={{ height: 6 }}>
                      <div className="progress-bar bg-primary" style={{ width: `${utilisationPct}%` }} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h6 className="mb-0 fw-bold"><i className="bi bi-coin me-2 text-primary"></i>Collateral Markers</h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm align-middle mb-0" style={{ fontSize: '0.78rem' }}>
                    <tbody>
                      {MARKERS.map(m => (
                        <tr key={m.denom}>
                          <td className="ps-3">
                            <div className="fw-semibold">{m.label}</div>
                            <code style={{ fontSize: '0.68rem' }}>{m.denom}</code>
                          </td>
                          <td className="text-end">
                            <div className="fw-semibold">{shortMoney(m.units * m.nav)}</div>
                            <small className="text-muted">{m.units} units</small>
                          </td>
                          <td className="text-end pe-3">
                            {m.pilot
                              ? <span className="badge bg-success bg-opacity-10 text-success" style={{ fontSize: '0.6rem' }}>IN PILOT</span>
                              : <span className="badge bg-light text-muted border" style={{ fontSize: '0.6rem' }}>RESERVED</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer bg-light text-muted" style={{ fontSize: '0.72rem' }}>
                  Marker state is read from Provenance <code style={{ fontSize: '0.68rem' }}>pio-mainnet-1</code> and is ZoneScan verifiable.
                </div>
              </div>
            </div>
          </div>

          {/* Workflow grouping — the deeper surfaces, grouped rather than scattered */}
          <div className="card border-0 shadow-sm mt-4">
            <div className="card-header bg-white border-bottom py-3">
              <h6 className="mb-0 fw-bold"><i className="bi bi-grid-1x2 me-2 text-primary"></i>Settlement Tools &amp; Records</h6>
              <small className="text-muted">Deeper operational surfaces, grouped under the hub.</small>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {WORKFLOW_TOOLS.map(t => (
                  <div key={t.to} className="col-12 col-lg-4">
                    <Link to={t.to} className="text-decoration-none">
                      <div className="border rounded-3 p-3 h-100 d-flex gap-3 align-items-start">
                        <div className={`p-2 rounded-3 bg-${t.tone} bg-opacity-10 flex-shrink-0`}>
                          <i className={`bi ${t.icon} text-${t.tone}`} style={{ fontSize: '1.2rem' }}></i>
                        </div>
                        <div>
                          <div className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                            {t.title}<i className="bi bi-arrow-right ms-2" style={{ fontSize: '0.75rem' }}></i>
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.78rem' }}>{t.desc}</div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════ TAB: SETTLEMENT STATUS ══════════════════ */}
      {activeTab === 'status' && (
        <>
          <div className="row g-3 mb-4">
            <KpiCard icon="bi-patch-check-fill" label="Settled"       tone="success" value={settledCount}   sub={`${shortMoney(settledValue)} across the pilot window`} />
            <KpiCard icon="bi-arrow-repeat"     label="In Flight"     tone="primary" value={inFlightCount}  sub={inFlightCount ? 'Currently mid-phase' : 'None open'} />
            <KpiCard icon="bi-percent"          label="Avg Repo Rate" tone="info"    value={`${avgRate}%`}  sub="ACT/360 day count" />
            <KpiCard icon="bi-shield-check"     label="Roll-backs"    tone="success" value="0"              sub="No partial settlements recorded" />
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h6 className="mb-0 fw-bold"><i className="bi bi-list-check me-2 text-primary"></i>Settlement Activity</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr style={{ fontSize: '0.76rem' }}>
                      <th className="ps-3">Settlement ID</th>
                      <th>Counterparty</th>
                      <th className="text-end">Notional</th>
                      <th className="text-end">Rate</th>
                      <th>Phase</th>
                      <th>Status</th>
                      <th className="pe-3">Close-out receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RECENT_SETTLEMENTS.map(s => (
                      <tr key={s.id} style={{ fontSize: '0.8rem' }}>
                        <td className="ps-3">
                          <code style={{ fontSize: '0.7rem' }}>{s.id}</code>
                          <div><small className="text-muted">{fmtDateTime(s.at)}</small></div>
                        </td>
                        <td>{s.counterparty}</td>
                        <td className="text-end fw-bold">{money(s.notional)}</td>
                        <td className="text-end">{s.rate.toFixed(2)}%</td>
                        <td><span className="badge bg-light text-dark border" style={{ fontSize: '0.66rem' }}>{s.phase}</span></td>
                        <td>
                          {s.status === 'SETTLED'
                            ? <span className="badge bg-success" style={{ fontSize: '0.66rem' }}><i className="bi bi-patch-check-fill me-1"></i>Settled</span>
                            : <span className="badge bg-primary" style={{ fontSize: '0.66rem' }}><i className="bi bi-arrow-repeat me-1"></i>In flight</span>}
                        </td>
                        <td className="pe-3">
                          {s.receipt
                            ? <code style={{ fontSize: '0.68rem' }}>{s.receipt}</code>
                            : <small className="text-muted">Pending finality</small>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              Settlement is atomic — the collateral leg and the cash leg settle in a single Daml transaction, or neither does. A close-out receipt is only issued once both legs confirm.
            </div>
          </div>

          {liveSummary && (
            <div className="card border-0 shadow-sm mt-4">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-broadcast me-2 text-primary"></i>Live Ledger Summary</h6>
              </div>
              <div className="card-body">
                <div className="row g-3" style={{ fontSize: '0.82rem' }}>
                  {[
                    { k: 'Network',          v: liveSummary.network ?? ENTITY.network },
                    { k: 'Active contracts', v: liveSummary.activeContracts ?? '—' },
                    { k: 'Total events',     v: liveSummary.totalEvents ?? '—' },
                    { k: 'Last event',       v: liveSummary.lastEventAt ? fmtDateTime(liveSummary.lastEventAt) : '—' },
                  ].map(r => (
                    <div key={r.k} className="col-6 col-lg-3">
                      <div className="text-muted text-uppercase" style={{ fontSize: '0.66rem', letterSpacing: '0.5px', fontWeight: 700 }}>{r.k}</div>
                      <div className="fw-bold">{r.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════ TAB: NETWORK HEALTH ══════════════════ */}
      {activeTab === 'network' && (
        <>
          <div className="row g-3 mb-4">
            <KpiCard icon="bi-hdd-network"  label="Rails Monitored" tone="primary" value={NETWORK_NODES.length} sub="Ledger, collateral, custody, and cash" />
            <KpiCard icon="bi-heart-pulse"  label="Healthy"         tone="success" value={NETWORK_NODES.length - degradedNodes.length} sub="Responding within tolerance" />
            <KpiCard icon="bi-exclamation-triangle" label="Degraded" tone={degradedNodes.length ? 'warning' : 'success'} value={degradedNodes.length} sub={degradedNodes.length ? 'Elevated latency, no failures' : 'None'} />
            <KpiCard icon="bi-activity"     label="Avg Uptime"      tone="info"    value={`${avgUptime}%`} sub="Rolling across monitored rails" />
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h6 className="mb-0 fw-bold"><i className="bi bi-hdd-network me-2 text-primary"></i>Network &amp; Rail Health</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr style={{ fontSize: '0.76rem' }}>
                      <th className="ps-3">Component</th>
                      <th>Layer</th>
                      <th className="text-end">Uptime</th>
                      <th className="text-end">Latency</th>
                      <th>State</th>
                      <th className="pe-3">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {NETWORK_NODES.map(n => (
                      <tr key={n.name} style={{ fontSize: '0.8rem' }}>
                        <td className="ps-3 fw-semibold">{n.name}</td>
                        <td><span className="badge bg-light text-dark border" style={{ fontSize: '0.66rem' }}>{n.layer}</span></td>
                        <td className="text-end fw-semibold">{n.uptime.toFixed(2)}%</td>
                        <td className="text-end">{n.latencyMs} ms</td>
                        <td>
                          <span className="d-inline-flex align-items-center gap-2">
                            <StateDot state={n.state} />
                            <span className={n.state === 'healthy' ? 'text-success' : 'text-warning'} style={{ textTransform: 'capitalize' }}>{n.state}</span>
                          </span>
                        </td>
                        <td className="text-muted pe-3" style={{ fontSize: '0.76rem' }}>{n.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              A degraded rail does not settle a transaction on partial information — the gate for that phase fails closed and the draw is held until the rail recovers.
            </div>
          </div>
        </>
      )}

      {/* ══════════════════ TAB: EXCEPTION MONITOR ══════════════════ */}
      {activeTab === 'exceptions' && (
        <>
          <div className="row g-3 mb-4">
            <KpiCard icon="bi-exclamation-triangle-fill" label="Open"          tone={openExceptions.length ? 'danger' : 'success'} value={openExceptions.length} sub={openExceptions.length ? 'Awaiting resolution' : 'Nothing outstanding'} />
            <KpiCard icon="bi-calendar-week"             label="This Week"     tone="primary" value={currentExceptions.length} sub="Raised since the week began" />
            <KpiCard icon="bi-archive"                   label="Archived"      tone="secondary" value={archivedExceptions.length} sub="Prior weeks, retained for audit" />
            <KpiCard icon="bi-shield-check"              label="Partial Settlements" tone="success" value="0" sub="Every exception failed closed" />
          </div>

          <div className="alert alert-light border py-2 mb-3" style={{ fontSize: '0.8rem' }}>
            <i className="bi bi-info-circle me-1"></i>
            Exceptions are recorded, not suppressed. When a gate fails, the draw is held in place — it is never partially settled — and the record below stays in the audit history after resolution.
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6 className="mb-0 fw-bold">
                <i className="bi bi-exclamation-triangle-fill me-2 text-danger"></i>
                {showArchived ? 'Archived Exceptions' : 'Current Week Exceptions'}
              </h6>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowArchived(!showArchived)}>
                <i className={`bi ${showArchived ? 'bi-calendar-week' : 'bi-archive'} me-1`}></i>
                {showArchived ? 'Show current week' : `Show archive (${archivedExceptions.length})`}
              </button>
            </div>
            <div className="card-body p-0">
              {visibleExceptions.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-shield-check d-block mb-2 text-success" style={{ fontSize: '1.8rem' }}></i>
                  {showArchived ? 'No archived exceptions.' : 'No exceptions raised this week.'}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr style={{ fontSize: '0.76rem' }}>
                        <th className="ps-3">Raised</th>
                        <th>Phase</th>
                        <th>Code</th>
                        <th>Severity</th>
                        <th>Detail</th>
                        <th className="pe-3">Resolution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleExceptions.map((e, i) => (
                        <tr key={`${e.code}-${i}`} style={{ fontSize: '0.8rem' }}>
                          <td className="ps-3" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.at)}</td>
                          <td><span className="badge bg-light text-dark border" style={{ fontSize: '0.66rem' }}>{e.phase}</span></td>
                          <td><code style={{ fontSize: '0.7rem' }}>{e.code}</code></td>
                          <td><span className={`badge bg-${SEVERITY_TONE[e.severity]}`} style={{ fontSize: '0.64rem' }}>{e.severity}</span></td>
                          <td className="text-muted" style={{ fontSize: '0.76rem' }}>{e.detail}</td>
                          <td className="pe-3">
                            {e.resolved
                              ? <><span className="badge bg-success" style={{ fontSize: '0.64rem' }}>Resolved</span><div><small className="text-muted">{fmtDateTime(e.resolvedAt)}</small></div></>
                              : <span className="badge bg-danger" style={{ fontSize: '0.64rem' }}>Open</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              Records archive automatically at the start of each week and remain available here for audit.
              Fuller operational failure analysis lives in <Link to="/bread/operations-management/production-analytics">Bread → Production Analytics</Link>.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SettlementOperationsHub;
